/**
 * Tag parsing and folder hierarchy. Keep the pattern in sync with the backend
 * (internal/domain/note/tags.go).
 */
export const MAX_TAG_LENGTH = 100;

const TAG_PATTERN = /(?:^|[\s([{,;>])#(\p{L}[\p{L}\p{N}_-]*(?::[\p{L}\p{N}_][\p{L}\p{N}_-]*)*)/gu;

/** Extracts unique tags (case-insensitive, first spelling wins) in order of appearance. */
export function parseTags(...texts: string[]): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const text of texts) {
    for (const match of text.matchAll(TAG_PATTERN)) {
      const tag = (match[1] ?? '').replace(/-+$/u, '');
      if (Array.from(tag).length > MAX_TAG_LENGTH) continue;
      const key = tagKey(tag);
      if (seen.has(key)) continue;
      seen.add(key);
      tags.push(tag);
    }
  }
  return tags;
}

/** Normalised lookup key of a tag or folder path. */
export function tagKey(tag: string): string {
  return tag.toLocaleLowerCase('de');
}

/** "A:B" -> ["A", "B"] */
export function tagPath(tag: string): string[] {
  return tag.split(':');
}

export interface Folder {
  /** Normalised full path, e.g. "einkaufen:supermarkt". */
  key: string;
  /** Display name of the last segment, e.g. "Supermarkt". */
  name: string;
  /** Display path, e.g. "Einkaufen:Supermarkt". */
  path: string;
  depth: number;
  /** Number of notes in this folder or any subfolder. */
  count: number;
  children: Folder[];
}

/** Builds the folder tree from the tags of all notes. */
export function buildFolderTree(tagLists: readonly (readonly string[])[]): Folder[] {
  const byKey = new Map<string, Folder>();
  const roots: Folder[] = [];
  for (const tags of tagLists) {
    const counted = new Set<string>();
    for (const tag of tags) {
      const segments = tagPath(tag);
      let parent: Folder | undefined;
      segments.forEach((name, i) => {
        const path = segments.slice(0, i + 1).join(':');
        const key = tagKey(path);
        let folder = byKey.get(key);
        if (!folder) {
          folder = { key, name, path, depth: i, count: 0, children: [] };
          byKey.set(key, folder);
          (parent ? parent.children : roots).push(folder);
        }
        if (!counted.has(key)) {
          counted.add(key);
          folder.count++;
        }
        parent = folder;
      });
    }
  }
  const sort = (list: Folder[]) => {
    list.sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
    list.forEach((f) => sort(f.children));
  };
  sort(roots);
  return roots;
}

/** Finds a folder by its key in a tree. */
export function findFolder(tree: readonly Folder[], key: string): Folder | undefined {
  for (const folder of tree) {
    if (folder.key === key) return folder;
    if (key.startsWith(`${folder.key}:`)) return findFolder(folder.children, key);
  }
  return undefined;
}

/** Whether a note with the given tags belongs into the folder (or one of its subfolders). */
export function inFolder(tags: readonly string[], folderKey: string): boolean {
  return tags.some((t) => {
    const key = tagKey(t);
    return key === folderKey || key.startsWith(`${folderKey}:`);
  });
}

/** Returns the tag fragment directly before the caret, e.g. "Eink" for "… #Eink|". */
export function tagQueryBefore(textBeforeCaret: string): string | null {
  const m = /(?:^|[\s([{,;>])#([\p{L}\p{N}_:-]*)$/u.exec(textBeforeCaret);
  return m ? (m[1] ?? '') : null;
}

/** Suggests known tags matching a query, prefix matches of any path segment first. */
export function suggestTags(known: readonly string[], query: string, limit = 8): string[] {
  const q = tagKey(query);
  const scored: { tag: string; score: number }[] = [];
  for (const tag of known) {
    const key = tagKey(tag);
    if (key === q) continue;
    let score: number;
    if (key.startsWith(q)) score = 0;
    else if (key.split(':').some((s) => s.startsWith(q))) score = 1;
    else if (key.includes(q)) score = 2;
    else continue;
    scored.push({ tag, score });
  }
  scored.sort((a, b) => a.score - b.score || a.tag.localeCompare(b.tag, 'de'));
  return scored.slice(0, limit).map((s) => s.tag);
}

/** All distinct tags including their parent paths ("A:B" also yields "A"). */
export function allTagPaths(tagLists: readonly (readonly string[])[]): string[] {
  const seen = new Map<string, string>();
  for (const tags of tagLists) {
    for (const tag of tags) {
      const segments = tagPath(tag);
      for (let i = 1; i <= segments.length; i++) {
        const path = segments.slice(0, i).join(':');
        const key = tagKey(path);
        if (!seen.has(key)) seen.set(key, path);
      }
    }
  }
  return [...seen.values()];
}
